import {create} from 'zustand';
import {DistributionRecord, CreateDeliveryForm} from '../models/distribution.model';

interface DistributionUIState {
  // Tab Management
  activeTab: 'history' | 'stats';
  setActiveTab: (tab: 'history' | 'stats') => void;

  // Delivery Modal State
  isDeliveryModalOpen: boolean;
  openDeliveryModal: () => void;
  closeDeliveryModal: () => void;

  // Marketer Delivery Form State
  deliveryFormData: CreateDeliveryForm;
  deliveryFormErrors: Record<string, string>;
  isSubmittingDelivery: boolean;
  selectedSerials: string[];
  bulkSerials: string[];

  // Marketer Delivery Form Actions
  setDeliveryFormData: (data: Partial<CreateDeliveryForm>) => void;
  setDeliveryFormErrors: (errors: Record<string, string>) => void;
  setIsSubmittingDelivery: (submitting: boolean) => void;
  setSelectedSerials: (serials: string[]) => void;
  setBulkSerials: (serials: string[]) => void;
  resetDeliveryForm: () => void;

  // Search States
  isSearchingMarketers: boolean;
  isSearchingProducts: boolean;
  setIsSearchingMarketers: (searching: boolean) => void;
  setIsSearchingProducts: (searching: boolean) => void;

  // Distribution Table State
  selectedRecord: DistributionRecord | null;
  isDetailsModalOpen: boolean;
  setSelectedRecord: (record: DistributionRecord | null) => void;
  openDetailsModal: (record: DistributionRecord) => void;
  closeDetailsModal: () => void;

  // Filter State
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;

  // Utility Actions
  reset: () => void;
}

const initialDeliveryFormData: CreateDeliveryForm = {
  marketerId: '',
  productId: '',
  quantity: 0,
  serials: []
};

export const useDistributionUIStore = create<DistributionUIState>((set) => ({
  // Tab Management
  activeTab: 'history',
  setActiveTab: (tab) => set({activeTab: tab}),

  // Delivery Modal State
  isDeliveryModalOpen: false,
  openDeliveryModal: () => set({
    isDeliveryModalOpen: true,
    selectedSerials: [],
    bulkSerials: []
  }),
  closeDeliveryModal: () => set({
    isDeliveryModalOpen: false,
    deliveryFormData: initialDeliveryFormData,
    deliveryFormErrors: {},
    selectedSerials: [],
    bulkSerials: []
  }),

  // Marketer Delivery Form State
  deliveryFormData: initialDeliveryFormData,
  deliveryFormErrors: {},
  isSubmittingDelivery: false,
  selectedSerials: [],
  bulkSerials: [],

  // Marketer Delivery Form Actions
  setDeliveryFormData: (data) => set((state) => ({
    deliveryFormData: {...state.deliveryFormData, ...data}
  })),
  setDeliveryFormErrors: (errors) => set({deliveryFormErrors: errors}),
  setIsSubmittingDelivery: (submitting) => set({isSubmittingDelivery: submitting}),
  setSelectedSerials: (serials) => set({selectedSerials: serials}),
  setBulkSerials: (serials) => set({bulkSerials: serials}),
  resetDeliveryForm: () => set({
    deliveryFormData: initialDeliveryFormData,
    deliveryFormErrors: {},
    isSubmittingDelivery: false,
    selectedSerials: [],
    bulkSerials: []
  }),

  // Search States
  isSearchingMarketers: false,
  isSearchingProducts: false,
  setIsSearchingMarketers: (searching) => set({isSearchingMarketers: searching}),
  setIsSearchingProducts: (searching) => set({isSearchingProducts: searching}),

  // Distribution Table State
  selectedRecord: null,
  isDetailsModalOpen: false,
  setSelectedRecord: (record) => set({selectedRecord: record}),
  openDetailsModal: (record) => set({
    isDetailsModalOpen: true,
    selectedRecord: record
  }),
  closeDetailsModal: () => set({
    isDetailsModalOpen: false,
    selectedRecord: null
  }),

  // Filter State
  showFilters: false,
  setShowFilters: (show) => set({showFilters: show}),

  // Utility Actions
  reset: () => set({
    activeTab: 'history',
    isDeliveryModalOpen: false,
    deliveryFormData: initialDeliveryFormData,
    deliveryFormErrors: {},
    isSubmittingDelivery: false,
    selectedSerials: [],
    bulkSerials: [],
    isSearchingMarketers: false,
    isSearchingProducts: false,
    selectedRecord: null,
    isDetailsModalOpen: false,
    showFilters: false
  })
}));
